import { useHistory } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@g17eco/ui-modern/components/ui/dialog';
import { generateUrl } from '@routes/util';

import { useGetEvidenceLinkedActivitiesQuery } from '../../api/emissions';
import { ACTIVITY_TYPE_LABELS } from '../../config/activityTypeBranding';
import { emissionsTrackerRoutes } from '../../routes';
import type { EmissionEvidenceListItem } from '../../types/emissions';

interface LinkedActivitiesModalProps {
  initiativeId: string;
  evidence: EmissionEvidenceListItem | null;
  onClose: () => void;
}

const formatPeriod = (start: string, end: string): string => {
  const formatter = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`;
};

export const LinkedActivitiesModal = ({ initiativeId, evidence, onClose }: LinkedActivitiesModalProps) => {
  const history = useHistory();
  // Skip the query when no evidence is selected so we don't fetch on every
  // page render — the modal mounts once and the query fires the moment
  // `evidence` flips to a real item.
  const { data, isFetching, error } = useGetEvidenceLinkedActivitiesQuery(
    { initiativeId, evidenceId: evidence?._id ?? '' },
    { skip: !evidence },
  );

  const activities = data ?? [];

  const handleNavigate = (activityId: string) => {
    history.push(
      generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITY_EDIT, { initiativeId, activityId }),
    );
    onClose();
  };

  return (
    <Dialog open={!!evidence} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className='tw:sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Linked activities</DialogTitle>
          <DialogDescription>
            {evidence ? (
              <>
                <strong>{evidence.originalName}</strong> is referenced by the activities below.
                Detach it from each before deleting.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {isFetching ? (
          <div className='tw:text-sm tw:text-ThemeTextMedium'>Loading activities…</div>
        ) : error ? (
          <div className='tw:text-sm tw:text-ThemeAccentDanger' role='alert'>
            Couldn&apos;t load linked activities. Try again or refresh the page.
          </div>
        ) : activities.length === 0 ? (
          <div className='tw:text-sm tw:text-ThemeTextLight'>
            No activities currently link this evidence.
          </div>
        ) : (
          <ul className='tw:space-y-2 tw:list-none tw:pl-0 tw:max-h-[60vh] tw:overflow-y-auto'>
            {activities.map((activity) => {
              const label = activity.name ?? ACTIVITY_TYPE_LABELS[activity.activityType] ?? activity.activityType;
              return (
                <li
                  key={activity._id}
                  className='tw:flex tw:items-center tw:gap-3 tw:rounded-md tw:border tw:border-border tw:p-3'
                >
                  <div className='tw:flex-1 tw:min-w-0'>
                    <div className='tw:flex tw:flex-wrap tw:items-center tw:gap-2'>
                      <span className='tw:truncate tw:text-sm tw:font-medium'>{label}</span>
                      <Badge variant='ghost' className='tw:text-[10px] tw:uppercase'>
                        {activity.scope}
                      </Badge>
                    </div>
                    <div className='tw:text-xs tw:text-ThemeTextLight tw:mt-0.5'>
                      {formatPeriod(activity.periodStart, activity.periodEnd)}
                    </div>
                  </div>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => handleNavigate(activity._id)}
                    aria-label={`Open ${label}`}
                  >
                    Open
                    <ArrowRight className='tw:size-3.5' />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
